import React, { useState, useRef, useEffect } from 'react';
import { Box, Button } from '@mui/material';

const ButtonBlock = ({ data, isSelected, onUpdate, previewMode, blockId, onSelectBlock }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(data.text || '');
  const inputRef = useRef(null);
  const {
    text = 'Click Here',
    backgroundColor = '#007bff',
    color = '#ffffff',
    borderRadius = 5,
    padding = '12px 24px',
    fontSize = 16,
    fontWeight = 'bold',
    fontFamily = 'Arial, sans-serif',
    alignment = 'center',
    textAlign = 'center',
    fullWidth = false,
    marginTop = 15,
    marginBottom = 15,
  } = data;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Синхронизируем текст кнопки при изменении данных извне
  useEffect(() => {
    if (!isEditing) {
      setEditText(text);
    }
  }, [text, isEditing]);

  const getResponsiveFontSize = () => {
    switch (previewMode) {
      case 'mobile':
        return Math.max(fontSize * 0.9, 14);
      case 'tablet':
        return Math.max(fontSize * 0.95, 15);
      default:
        return fontSize;
    }
  };

  const getResponsivePadding = () => {
    if (previewMode === 'mobile') {
      return '10px 20px';
    }
    return padding;
  };

  const containerStyles = {
    textAlign: alignment || textAlign,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    border: isSelected ? '1px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : 0,
    padding: isSelected ? '5px' : 0,
  };

  const buttonStyles = {
    backgroundColor,
    color,
    borderRadius: `${borderRadius}px`,
    padding: getResponsivePadding(),
    fontSize: `${getResponsiveFontSize()}px`,
    fontWeight,
    fontFamily,
    textTransform: 'none',
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-block',
    lineHeight: 1.2,
    minWidth: fullWidth ? '100%' : 'auto',
    width: fullWidth ? '100%' : 'auto',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: adjustColor(backgroundColor, -20),
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
    '&:active': {
      transform: 'translateY(1px)',
    },
  };

  // Функция для затемнения цвета при наведении
  function adjustColor(color, amount) {
    if (color.startsWith('#')) {
      const num = parseInt(color.slice(1), 16);
      const r = Math.max(0, Math.min(255, (num >> 16) + amount));
      const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amount));
      const b = Math.max(0, Math.min(255, (num & 0x0000FF) + amount));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }
    return color;
  }

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // В режиме редактирования кнопки не должны быть активными
    // Только показываем как они будут выглядеть
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setEditText(text);
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (onUpdate && editText !== text) {
      onUpdate({ text: editText });
      
      // Обновляем selectedBlock если этот блок выбран
      if (onSelectBlock && blockId) {
        onSelectBlock({
          id: blockId,
          type: 'button',
          data: { ...data, text: editText }
        });
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setEditText(text);
    }
  };

  if (isEditing) {
    return (
      <Box sx={containerStyles}>
        <input
          ref={inputRef}
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            ...buttonStyles,
            border: '2px solid #007bff',
            outline: 'none',
          }}
          placeholder="Enter button text..."
        />
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <Button
        sx={buttonStyles}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        fullWidth={fullWidth}
      >
        {text || 'Button Text'}
      </Button>
    </Box>
  );
};

export default ButtonBlock; 